import type { RecipeSort } from '@recipes/contracts';
import {
  Badge,
  Button,
  EmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@recipes/ui';
import { Plus, RotateCcw, SlidersHorizontal, Trash2, WifiOff, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { Illustration } from '@/features/appearance/illustration';
import { ConfirmDialog } from '@/components/forms/confirm-dialog';
import { LiveRegion, announce } from '@/components/feedback/live-region';
import { PageHeader } from '@/components/layout/page-header';
import { RecipeCard } from '@/components/recipes/recipe-card';
import {
  activeFilterCount,
  filtersFromSearchParams,
  filtersToQuery,
  filtersToSearchParams,
  type RecipeFilterState,
} from '@/features/recipes/filter-state';
import {
  usePurgeRecipe,
  useRecipeList,
  useRestoreRecipe,
  useToggleFavorite,
} from '@/features/recipes/queries';
import { RecipeFilterSheet } from '@/features/recipes/recipe-filter-sheet';
import { RecipeSearchBox } from '@/features/recipes/recipe-search-box';
import { toastError } from '@/lib/errors';
import { toast } from '@/components/feedback/toaster';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { startSyncManager } from '@/lib/offline/queue';

const SORTS: RecipeSort[] = [
  'relevance',
  'title',
  'rating',
  'lastCooked',
  'recentlyAdded',
  'recentlyUpdated',
  'prepTime',
  'totalTime',
];

export function RecipeLibraryPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [purgeTarget, setPurgeTarget] = useState<string | null>(null);
  const online = useOnlineStatus();
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => startSyncManager(), []);

  const query = useRecipeList(filtersToQuery(filters));
  const toggleFavorite = useToggleFavorite();
  const restore = useRestoreRecipe();
  const purge = usePurgeRecipe();

  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  const offlineCopy = query.data?.pages[0]?.offlineCopy ?? false;
  const total = query.data?.pages[0]?.total;

  const update = (next: RecipeFilterState) =>
    setSearchParams(filtersToSearchParams(next), { replace: true });

  // Infinite scroll
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !query.hasNextPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !query.isFetchingNextPage)
        void query.fetchNextPage();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [query, query.hasNextPage, query.isFetchingNextPage]);

  useEffect(() => {
    if (query.isSuccess)
      announce(t('recipes.library.resultsAnnounce', { count: total ?? items.length }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.dataUpdatedAt]);

  const activeCount = activeFilterCount(filters);

  return (
    <div className="mx-auto max-w-6xl p-4 md:p-6" data-testid="library-page">
      <LiveRegion />
      <PageHeader
        title={filters.trashed ? t('recipes.library.trashTitle') : t('recipes.library.title')}
        description={total !== undefined ? t('recipes.library.count', { count: total }) : undefined}
        actions={
          <>
            <Button
              variant={filters.trashed ? 'secondary' : 'ghost'}
              onClick={() => update({ ...filters, trashed: !filters.trashed })}
              aria-pressed={filters.trashed}
              data-testid="trash-toggle"
            >
              <Trash2 aria-hidden /> {t('recipes.library.trash')}
            </Button>
            <Button asChild data-testid="new-recipe-button">
              <Link to="/recipes/new">
                <Plus aria-hidden /> {t('recipes.library.new')}
              </Link>
            </Button>
          </>
        }
      />

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <RecipeSearchBox
          value={filters.q}
          onSearch={(q) => update({ ...filters, q })}
          onPickSuggestion={(s) => {
            if (s.kind === 'tag')
              update({ ...filters, tags: Array.from(new Set([...filters.tags, s.label])) });
            else if (s.kind === 'category' && s.id)
              update({
                ...filters,
                categoryIds: Array.from(new Set([...filters.categoryIds, s.id])),
              });
            else if (s.kind === 'ingredient') update({ ...filters, q: s.label });
            else update({ ...filters, q: s.label });
          }}
          className="flex-1"
        />
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="h-11"
            onClick={() => setSheetOpen(true)}
            data-testid="filter-button"
          >
            <SlidersHorizontal aria-hidden /> {t('common.filter')}
            {activeCount > 0 && (
              <Badge variant="default" className="ml-1 px-1.5">
                {activeCount}
              </Badge>
            )}
          </Button>
          <Select
            value={filters.sort}
            onValueChange={(sort) => update({ ...filters, sort: sort as RecipeSort })}
          >
            <SelectTrigger
              className="h-11 w-44"
              aria-label={t('common.sort')}
              data-testid="sort-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORTS.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`recipes.sort.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {(filters.tags.length > 0 ||
        filters.availableIngredients.length > 0 ||
        filters.favorite ||
        filters.baseRecipe) && (
        <ul className="mb-4 flex flex-wrap gap-1.5" aria-label={t('recipes.filters.active')}>
          {filters.favorite && (
            <li>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => update({ ...filters, favorite: false })}
              >
                {t('recipes.filters.favoritesOnly')} <X aria-hidden />
              </Button>
            </li>
          )}
          {filters.baseRecipe && (
            <li>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => update({ ...filters, baseRecipe: false })}
              >
                {t('baseRecipes.filter')} <X aria-hidden />
              </Button>
            </li>
          )}
          {filters.tags.map((tag) => (
            <li key={tag}>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => update({ ...filters, tags: filters.tags.filter((x) => x !== tag) })}
              >
                #{tag} <X aria-hidden />
              </Button>
            </li>
          ))}
          {filters.availableIngredients.map((ing) => (
            <li key={ing}>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  update({
                    ...filters,
                    availableIngredients: filters.availableIngredients.filter((x) => x !== ing),
                  })
                }
              >
                {ing} <X aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {(offlineCopy || !online) && (
        <p
          role="status"
          className="mb-3 inline-flex items-center gap-2 rounded-md bg-muted px-3 py-1.5 text-sm"
        >
          <WifiOff className="size-4" aria-hidden /> {t('offline.libraryHint')}
        </p>
      )}

      {query.isPending ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      ) : query.isError ? (
        <EmptyState
          title={t('app.unexpectedError')}
          description={t('recipes.library.loadError')}
          action={
            <Button variant="outline" onClick={() => void query.refetch()}>
              {t('app.retry')}
            </Button>
          }
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={filters.trashed ? <Trash2 /> : undefined}
          illustration={filters.trashed ? undefined : <Illustration name="recipes" />}
          title={
            filters.trashed
              ? t('recipes.library.trashEmpty')
              : filters.q || activeCount > 0
                ? t('recipes.library.noMatches')
                : t('recipes.library.empty')
          }
          description={
            filters.trashed
              ? undefined
              : filters.q || activeCount > 0
                ? t('recipes.library.noMatchesHint')
                : t('recipes.library.emptyHint')
          }
          action={
            filters.trashed ? undefined : filters.q || activeCount > 0 ? (
              <Button
                variant="outline"
                onClick={() =>
                  update({
                    ...filters,
                    q: '',
                    tags: [],
                    categoryIds: [],
                    cuisine: '',
                    mealType: '',
                    difficulty: '',
                    minRating: null,
                    favorite: false,
                    baseRecipe: false,
                    maxTotalMinutes: null,
                    dietary: [],
                    excludeAllergens: [],
                    availableIngredients: [],
                  })
                }
              >
                {t('recipes.filters.reset')}
              </Button>
            ) : (
              <div className="flex flex-wrap justify-center gap-2">
                <Button asChild>
                  <Link to="/recipes/new">{t('recipes.library.new')}</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/add/import">{t('recipes.library.import')}</Link>
                </Button>
              </div>
            )
          }
          data-testid="library-empty"
        />
      ) : (
        <>
          <ul
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="recipe-grid"
          >
            {items.map((recipe) => (
              <li key={recipe.id}>
                <RecipeCard
                  recipe={recipe}
                  offlineCopy={offlineCopy}
                  className="h-full"
                  onToggleFavorite={
                    filters.trashed
                      ? undefined
                      : (r, next) => toggleFavorite.mutate({ id: r.id, favorite: next })
                  }
                  actions={
                    filters.trashed ? (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          loading={restore.isPending && restore.variables === recipe.id}
                          onClick={() =>
                            restore.mutate(recipe.id, {
                              onSuccess: () => toast.success(t('recipes.library.restored')),
                              onError: (e) => toastError(e),
                            })
                          }
                          data-testid="restore-button"
                        >
                          <RotateCcw aria-hidden /> {t('recipes.library.restore')}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setPurgeTarget(recipe.id)}
                          data-testid="purge-button"
                        >
                          <Trash2 aria-hidden /> {t('recipes.library.purge')}
                        </Button>
                      </>
                    ) : undefined
                  }
                />
              </li>
            ))}
          </ul>
          <div ref={sentinel} className="h-8" aria-hidden />
          {query.hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => void query.fetchNextPage()}
                loading={query.isFetchingNextPage}
              >
                {t('common.loadMore')}
              </Button>
            </div>
          )}
        </>
      )}

      <RecipeFilterSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        value={filters}
        onApply={update}
      />
      <ConfirmDialog
        open={purgeTarget !== null}
        onOpenChange={(open) => !open && setPurgeTarget(null)}
        title={t('recipes.library.purgeTitle')}
        description={t('recipes.library.purgeDescription')}
        confirmLabel={t('recipes.library.purge')}
        destructive
        loading={purge.isPending}
        onConfirm={() => {
          if (!purgeTarget) return;
          purge.mutate(purgeTarget, {
            onSuccess: () => {
              setPurgeTarget(null);
              toast.success(t('recipes.library.purged'));
            },
            onError: (e) => toastError(e),
          });
        }}
      />
    </div>
  );
}
